// src/pages/LocationApprovals.js - owner approves shop name-board photo locations
import React, { useState, useEffect, useCallback } from 'react';
import { get, post } from '../utils/api';
import { Spinner } from '../components/Shared';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { defaultIcon, staleIcon } from '../utils/leafletIcons';
import LocationHistory from './LocationHistory';

const POOR_ACCURACY_M = 50;
const FAR_FROM_OLD_M = 200;

export default function LocationApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [tab, setTab] = useState('pending');

  const load = useCallback(async () => {
    try {
      const d = await get('/api/party-locations/pending');
      setItems((d && d.submissions) || []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function act(id, kind) {
    let body = {};
    if (kind === 'reject') {
      const reason = window.prompt('Reason for rejecting (optional)');
      if (reason === null) return;
      body = { reason };
    } else if (!window.confirm('Approve this location? It will replace the saved location for this party.')) return;
    setBusyId(id);
    try {
      await post('/api/party-locations/' + id + '/' + kind, body);
      setItems(list => list.filter(x => x.id !== id));
    } catch (e) { window.alert(e.message || 'Failed'); }
    finally { setBusyId(null); }
  }

  const tabBar = (
    <div className="flex gap-2 mb-4">
      {[['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']].map(([k, label]) => (
        <button key={k} onClick={() => setTab(k)}
          className={'px-4 py-2 rounded-lg text-sm border ' + (tab === k ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700')}>{label}{k === 'pending' && items.length > 0 ? ' (' + items.length + ')' : ''}</button>
      ))}
    </div>
  );
  if (tab !== 'pending') {
    return (
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="text-xl font-semibold mb-3">Location Approvals</h1>
        {tabBar}
        <LocationHistory status={tab} />
      </div>
    );
  }
  if (loading) return <Spinner />;

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold mb-3">Location Approvals</h1>
      {tabBar}
      <p className="text-sm text-gray-500 mb-4">Name-board photos from drivers. Approving saves the photo's GPS as the party's location.</p>
      {error && <div className="p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">{error} (this page is for the owner only)</div>}
      {!error && items.length === 0 && <div className="p-6 text-center text-gray-500">No pending submissions.</div>}
      {items.map(s => {
        const la = Number(s.lat), lo = Number(s.lng);
        const hasOld = s.current_lat != null && s.current_lng != null;
        const acc = s.accuracy_metres != null ? Number(s.accuracy_metres) : null;
        const dist = s.distance_m != null ? Number(s.distance_m) : null;
        const poor = acc === null || acc > POOR_ACCURACY_M;
        const far = dist !== null && dist > FAR_FROM_OLD_M;
        const addr = [s.address_line1, s.address_line2, s.area_name, s.city].filter(Boolean).join(', ');
        return (
          <div key={s.id} className="border rounded-xl p-3 mb-4 bg-white">
            <div className="flex flex-col md:flex-row gap-3">
              <img src={s.photo_url} alt="Name board" onClick={() => setPhoto(s.photo_url)}
                className="w-full md:w-64 h-48 object-cover rounded-lg cursor-pointer" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{s.party_name}</div>
                <div className="text-sm text-gray-600">{addr}</div>
                <div className="text-xs text-gray-500 mt-1">By {s.driver_name} - {new Date(s.created_at).toLocaleString()}</div>
                <div className="text-sm mt-2">
                  GPS accuracy: {acc === null ? 'unknown' : acc + ' m'}
                  {dist !== null && <span> - {dist} m from saved location</span>}
                  {!hasOld && <span> - no saved location yet</span>}
                </div>
                {poor && <div className="mt-2 p-2 rounded bg-amber-50 text-amber-800 text-sm">Poor or unknown GPS accuracy. Check the map carefully.</div>}
                {far && <div className="mt-2 p-2 rounded bg-amber-50 text-amber-800 text-sm">New pin is far from the saved location.</div>}
                <div className="mt-3 flex gap-2">
                  <button disabled={busyId === s.id} onClick={() => act(s.id, 'approve')}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-50">Approve</button>
                  <button disabled={busyId === s.id} onClick={() => act(s.id, 'reject')}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50">Reject</button>
                </div>
              </div>
            </div>
            <div className="mt-3 h-64 rounded-lg overflow-hidden">
              <MapContainer center={[la, lo]} zoom={18} style={{ height: '100%', width: '100%' }}>
                <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[la, lo]} icon={defaultIcon}><Popup>New (board photo)</Popup></Marker>
                {hasOld && <Marker position={[Number(s.current_lat), Number(s.current_lng)]} icon={staleIcon}><Popup>Saved location</Popup></Marker>}
              </MapContainer>
            </div>
          </div>
        );
      })}
      {photo && (
        <div onClick={() => setPhoto(null)} className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <img src={photo} alt="Name board" className="max-w-full max-h-full" />
        </div>
      )}
    </div>
  );
}
